import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCaption, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Building2, Calendar, FileText, MapPin, Target, X, Briefcase, Users, Clock } from "lucide-react";
import { format } from "date-fns";
import { fetchNpoRfqs } from "@/lib/api/campaign";
import {
  Dialog,
  DialogContent,
  DialogClose,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Opportunity } from "@/hooks/useOpportunitiesApi";

interface RFQResponse {
  id: string;
  rfq_id: string;
  npos_id: string;
  status: number;
  created_at: string;
  updated_at: string;
  reason: string | null;
  rfq: RFQ;
}

interface RFQ {
  id: string;
  corporate_id: string;
  campaign_id: string;
  meta: {
    additional_notes?: string;
  };
  status: number;
  created_at: string;
  updated_at: string;
  event_campaign: {
    name: string;
    meta: {
      goals?: string[];
      theme?: string;
      locations?: {
        state: string;
        country: string;
        district: string;
        meta?: {
          volunteers: number;
          details?: string;
        };
      }[];
      description?: string;
      opportunities?: string[];
      additional_details?: string;
    };
    active_from: string;
    active_to: string;
  };
  corporate: {
    id: string;
    org_name: string;
    poc_first_name?: string;
    profile_photo_url?: string | null;
  };
}

const getResponseStatusBadge = (status: number) => {
  switch (status) {
    case 0:
      return <Badge variant="outline">Awaiting Response</Badge>;
    case 1:
      return <Badge variant="secondary">In Review</Badge>;
    case 2:
      return <Badge variant="default">Accepted</Badge>;
    case 3:
      return <Badge variant="destructive">Rejected</Badge>;
    case 4:
      return <Badge variant="secondary">Partially Accepted</Badge>;
    default:
      return <Badge variant="outline">Unknown</Badge>;
  }
};

// Helper function to safely format dates
const safeFormatDate = (dateString: string | undefined | null) => {
  if (!dateString) return "N/A";
  try {
    return format(new Date(dateString), "MMM d, yyyy");
  } catch (error) {
    console.error("Error formatting date:", dateString, error);
    return "Invalid date";
  }
};

export default function NpoRfqPage({ session, profile }: { session: any; profile: any }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedResponse, setSelectedResponse] = useState<RFQResponse | null>(null);
  const [opportunitiesData, setOpportunitiesData] = useState<Opportunity[]>([]);
  const [loadingOpportunities, setLoadingOpportunities] = useState(false);
  const [opportunitiesPage, setOpportunitiesPage] = useState(1);
  const [hasMoreOpportunities, setHasMoreOpportunities] = useState(true);
  const [totalOpportunitiesCount, setTotalOpportunitiesCount] = useState(0);
  const OPPORTUNITIES_PER_PAGE = 3; // Number of opportunities to load per page
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const pageSize = 10;
  const navigate = useNavigate();

  const { 
    data, 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['npo-rfqs', currentPage],
    queryFn: () => fetchNpoRfqs(session?.access_token, currentPage, pageSize),
    enabled: !!session?.access_token,
  });

  const rfqResponses: RFQResponse[] = data?.data || [];
  const pagination = data?.pagination || { total: 0, page: 1, limit: pageSize, totalPages: 1 };

  // Effect to fetch opportunities when an RFQ is selected
  // Reset pagination state when a new response is selected
  useEffect(() => {
    if (selectedResponse) {
      setOpportunitiesPage(1);
      setOpportunitiesData([]);
      setHasMoreOpportunities(true);
      
      // Set the total count of opportunities
      if (selectedResponse?.rfq?.event_campaign?.meta?.opportunities) {
        const count = selectedResponse.rfq.event_campaign.meta.opportunities.length;
        setTotalOpportunitiesCount(count);
      } else {
        setTotalOpportunitiesCount(0);
      }
    }
  }, [selectedResponse]);

  // Load opportunities incrementally
  const fetchOpportunities = useCallback(async () => {
    if (!selectedResponse?.rfq?.event_campaign?.meta?.opportunities || 
        selectedResponse.rfq.event_campaign.meta.opportunities.length === 0 || 
        !hasMoreOpportunities) {
      return;
    }
    
    // Prevent multiple simultaneous fetches
    if (loadingOpportunities) {
      return;
    }
    
    setLoadingOpportunities(true);
    
    try {
      const allOpportunityIds = selectedResponse.rfq.event_campaign.meta.opportunities;
      const startIndex = (opportunitiesPage - 1) * OPPORTUNITIES_PER_PAGE;
      const endIndex = Math.min(startIndex + OPPORTUNITIES_PER_PAGE, allOpportunityIds.length);
      
      // Check if we've reached the end
      if (startIndex >= allOpportunityIds.length) {
        setHasMoreOpportunities(false);
        setLoadingOpportunities(false);
        return;
      }
      
      const currentBatchIds = allOpportunityIds.slice(startIndex, endIndex);
      const newOpportunities: Opportunity[] = [];
      
      // Fetch each opportunity in the current batch
      for (const oppId of currentBatchIds) {
        try {
          const response = await fetch(
            `${import.meta.env.VITE_CC_API_URL}/v1/opportunities/?id=${oppId}`,
            {
              headers: {
                Authorization: `Bearer ${session?.access_token}`,
                "Content-Type": "application/json",
              },
            }
          );
          
          if (response.ok) {
            const data = await response.json();
            if (data.data && data.data.length > 0) {
              newOpportunities.push(data.data[0]);
            }
          }
        } catch (error) {
          console.error(`Error fetching opportunity ${oppId}:`, error);
        }
      }
      
      // Update the opportunities data with the new opportunities
      setOpportunitiesData(prev => [...prev, ...newOpportunities]);
      
      // Check if we have more opportunities to load
      if (endIndex >= allOpportunityIds.length) {
        setHasMoreOpportunities(false);
      } else {
        setOpportunitiesPage(prev => prev + 1);
      }
    } catch (error) {
      console.error("Error fetching opportunities:", error);
    } finally {
      setLoadingOpportunities(false);
    }
  }, [selectedResponse, session?.access_token, opportunitiesPage, hasMoreOpportunities, OPPORTUNITIES_PER_PAGE, loadingOpportunities]);
  
  // Initial load of opportunities when dialog opens
  useEffect(() => {
    if (selectedResponse?.rfq?.event_campaign?.meta?.opportunities && 
        selectedResponse.rfq.event_campaign.meta.opportunities.length > 0 && 
        opportunitiesData.length === 0) {
      // Reset pagination state
      setOpportunitiesPage(1);
      setHasMoreOpportunities(true);
      // Fetch the first batch of opportunities
      fetchOpportunities();
    }
  }, [selectedResponse, fetchOpportunities, opportunitiesData.length]);
  
  // Setup scroll detection for loading more opportunities
  useEffect(() => {
    // Only setup the observer if we have more opportunities to load and we're in the opportunities tab
    if (!hasMoreOpportunities || !selectedResponse) return;
    
    // Create an intersection observer to detect when the user scrolls to the load more element
    const observer = new IntersectionObserver(
      (entries) => {
        // If the load more element is visible and we're not already loading
        if (entries[0].isIntersecting && !loadingOpportunities) {
          fetchOpportunities();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );
    
    // Start observing the load more element
    const currentLoadMoreRef = loadMoreRef.current;
    if (currentLoadMoreRef) {
      observer.observe(currentLoadMoreRef);
    }
    
    // Clean up the observer when the component unmounts or dependencies change
    return () => {
      if (currentLoadMoreRef) {
        observer.disconnect();
      }
    };
  }, [selectedResponse, hasMoreOpportunities, fetchOpportunities, loadingOpportunities]);
  
  // Re-observe when tab changes
  const handleTabChange = (value: string) => {
    if (value === "opportunities" && hasMoreOpportunities) {
      // Small delay to ensure the DOM is updated
      setTimeout(() => {
        const observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting && !loadingOpportunities) {
              fetchOpportunities();
            }
          },
          { threshold: 0.1, rootMargin: '100px' }
        );
        
        if (loadMoreRef.current) {
          observer.observe(loadMoreRef.current);
          
          // Clean up after a short period to avoid memory leaks
          setTimeout(() => observer.disconnect(), 2000);
        }
      }, 300);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const openRfqDetails = (response: RFQResponse) => {
    setSelectedResponse(response);
  };

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold mb-6">RFQ Requests</h1>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-destructive">
              Error loading RFQ requests. Please try again later.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold mb-6">RFQ Requests</h1>
      <Card>
        <CardHeader>
          <CardTitle>Requests for Quotation</CardTitle>
          <CardDescription>
            Review and respond to corporate partnership inquiries
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[250px]" />
                    <Skeleton className="h-4 w-[200px]" />
                  </div>
                </div>
              ))}
            </div>
          ) : rfqResponses.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Corporate</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Date Received</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rfqResponses.map((response) => (
                    <TableRow key={response.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openRfqDetails(response)}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <div className="flex items-center gap-2">
                            {response.rfq?.corporate?.profile_photo_url ? (
                              <img 
                                src={response.rfq?.corporate?.profile_photo_url} 
                                alt={response.rfq?.corporate?.org_name}
                                className="h-5 w-5 rounded-full object-cover"
                              />
                            ) : (
                              <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="text-xs text-primary font-medium">
                                  {response.rfq?.corporate?.org_name?.charAt(0)}
                                </span>
                              </div>
                            )}
                            {response.rfq?.corporate?.org_name || "Corporate Partner"}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          {response.rfq?.event_campaign?.name || "Campaign"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          {safeFormatDate(response?.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>{getResponseStatusBadge(response.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {(response.status === 0 || response.status === 5) && (
                            <Button 
                              size="sm" 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/dashboard/corporate-events/rfqs/${response.rfq_id}`);
                              }}
                            >
                              Respond
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={(e) => {
                              e.stopPropagation();
                              openRfqDetails(response);
                            }}
                          >
                            View Details
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {pagination.totalPages > 1 && (
                <div className="mt-4 flex justify-center">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                          className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                      
                      {[...Array(pagination.totalPages)].map((_, i) => {
                        const page = i + 1;
                        if (
                          page === 1 || 
                          page === pagination.totalPages || 
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <PaginationItem key={page}>
                              <PaginationLink 
                                isActive={page === currentPage}
                                onClick={() => handlePageChange(page)}
                              >
                                {page}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        }
                        
                        if (
                          (page === 2 && currentPage > 3) || 
                          (page === pagination.totalPages - 1 && currentPage < pagination.totalPages - 2)
                        ) {
                          return <PaginationItem key={page}>...</PaginationItem>;
                        }
                        
                        return null;
                      })}
                      
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => handlePageChange(Math.min(pagination.totalPages, currentPage + 1))}
                          className={currentPage === pagination.totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No RFQ requests found. When corporate partners send you requests for quotation, they will appear here.
            </div>
          )}
        </CardContent>
      </Card>

      {/* RFQ Details Dialog */}
      <Dialog open={!!selectedResponse} onOpenChange={(open) => !open && setSelectedResponse(null)}>
        {selectedResponse && (
          <DialogContent className="max-w-4xl p-0" closeButton={false}>
            <div className="p-6 pb-4">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-semibold">RFQ from {selectedResponse.rfq?.corporate?.org_name}</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Received on {safeFormatDate(selectedResponse.created_at)}
                  </p>
                  <div className="mt-2">
                    {getResponseStatusBadge(selectedResponse.status)}
                    {selectedResponse.status === 3 && selectedResponse.reason && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-100 rounded-md">
                        <p className="text-sm font-medium text-red-700">Rejection reason:</p>
                        <p className="text-sm text-red-600">{selectedResponse.reason}</p>
                      </div>
                    )}
                  </div>
                </div>
                <DialogClose className="rounded-full h-6 w-6 p-0 flex items-center justify-center hover:bg-muted">
                  <X className="h-4 w-4" />
                </DialogClose>
              </div>
            </div>
            
            <Tabs defaultValue="details" className="w-full" onValueChange={handleTabChange}>
              <TabsList className="grid w-full grid-cols-2 px-6">
                <TabsTrigger value="details">Campaign Details</TabsTrigger>
                <TabsTrigger value="opportunities">Opportunities ({totalOpportunitiesCount})</TabsTrigger>
              </TabsList>
              
              <div className="p-6 overflow-y-auto max-h-[70vh]">
                <TabsContent value="details" className="mt-0 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Campaign Information</h3>
                      <div className="space-y-3">
                        <div className="flex items-start gap-2">
                          <Briefcase className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="font-medium">Campaign Name</p>
                            <p className="text-muted-foreground">{selectedResponse.rfq?.event_campaign?.name || "N/A"}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="font-medium">Corporate</p>
                            <div className="flex items-center gap-2 mt-1">
                              {selectedResponse.rfq?.corporate?.profile_photo_url ? (
                                <img 
                                  src={selectedResponse.rfq?.corporate?.profile_photo_url} 
                                  alt={selectedResponse.rfq?.corporate?.org_name}
                                  className="h-6 w-6 rounded-full object-cover"
                                />
                              ) : (
                                <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                                  <span className="text-xs text-primary font-medium">
                                    {selectedResponse.rfq?.corporate?.org_name?.charAt(0)}
                                  </span>
                                </div>
                              )}
                              <p className="text-muted-foreground">{selectedResponse.rfq?.corporate?.org_name}</p>
                            </div>
                            {selectedResponse.rfq?.corporate?.poc_first_name && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Contact: {selectedResponse.rfq?.corporate?.poc_first_name}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="font-medium">Date Range</p>
                            <p className="text-muted-foreground">
                              {selectedResponse.rfq?.event_campaign?.active_from && selectedResponse.rfq?.event_campaign?.active_to
                                ? `${safeFormatDate(selectedResponse.rfq?.event_campaign?.active_from)} - ${safeFormatDate(selectedResponse.rfq?.event_campaign?.active_to)}`
                                : "N/A"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="font-medium">Status</p>
                            <div>{getResponseStatusBadge(selectedResponse.status)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Additional Details</h3>
                      <div className="space-y-3">
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="font-medium">Locations</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {selectedResponse.rfq?.event_campaign?.meta?.locations && selectedResponse.rfq?.event_campaign?.meta?.locations?.length > 0 ? (
                                selectedResponse.rfq?.event_campaign?.meta?.locations?.map((location, index) => (
                                  <div key={index} className="bg-slate-100 px-3 py-1.5 rounded-md text-sm mb-2 mr-2">
                                    <div className="font-medium">{location.district}, {location.state}, {location.country}</div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                      <span className="font-medium">Volunteers needed:</span> {location.meta?.volunteers}
                                    </div>
                                    {location.meta?.details && (
                                      <div className="text-xs mt-1">
                                        <span className="font-medium">Details:</span> {location.meta?.details}
                                      </div>
                                    )}
                                  </div>
                                ))
                              ) : (
                                <p className="text-muted-foreground">No locations specified</p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Target className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="font-medium">Goals</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {selectedResponse.rfq?.event_campaign?.meta?.goals && selectedResponse.rfq?.event_campaign?.meta?.goals?.length > 0 ? (
                                selectedResponse.rfq?.event_campaign?.meta?.goals?.map((goal, index) => (
                                  <div key={index} className="flex items-center gap-1.5 text-sm bg-slate-100 px-3 py-1.5 rounded-md">
                                    <Target className="h-3.5 w-3.5 text-muted-foreground" />
                                    {goal}
                                  </div>
                                ))
                              ) : (
                                <p className="text-muted-foreground">No goals specified</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Description</h3>
                    <Card className="p-4">
                      <p className="text-sm text-muted-foreground">
                        {selectedResponse.rfq?.event_campaign?.meta?.description || "No description provided."}
                      </p>
                    </Card>
                  </div>
                  
                  {selectedResponse.rfq?.meta?.additional_notes && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Additional Notes</h3>
                      <Card className="p-4">
                        <p className="text-sm text-muted-foreground">{selectedResponse.rfq?.meta?.additional_notes}</p>
                      </Card>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="opportunities" className="mt-0">
                  {opportunitiesData.length === 0 && loadingOpportunities ? (
                    <div className="space-y-4">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="flex items-center space-x-4">
                          <Skeleton className="h-12 w-12 rounded-md" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-[250px]" />
                            <Skeleton className="h-4 w-[200px]" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : opportunitiesData && opportunitiesData.length > 0 ? (
                    <div className="space-y-6">
                      {opportunitiesData.map((opportunity, index) => (
                        <div key={index} className="bg-slate-50/50 rounded-lg overflow-hidden border">
                          <div className="flex flex-col md:flex-row">
                            {opportunity.image_url ? (
                              <div className="w-full md:w-1/3 h-48 md:h-auto">
                                <img 
                                  src={opportunity.image_url} 
                                  alt={opportunity.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className="w-full md:w-1/3 h-48 md:h-auto bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                                <span className="text-white text-xl font-medium">
                                  {opportunity.name.charAt(0)}
                                </span>
                              </div>
                            )}
                            
                            <div className="p-4 flex-1">
                              <h4 className="text-lg font-semibold mb-2">{opportunity.name}</h4>
                              <p className="text-sm text-muted-foreground mb-4">{opportunity.community_need}</p>
                              
                              <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center gap-1.5">
                                  <Clock className="h-4 w-4 text-muted-foreground" />
                                  <span className="font-medium">Format:</span> {opportunity.format}
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <MapPin className="h-4 w-4 text-muted-foreground" />
                                  <span className="font-medium">Location:</span> {opportunity.location}
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Users className="h-4 w-4 text-muted-foreground" />
                                  <span className="font-medium">Focus Area:</span> {opportunity.focus_area}
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Target className="h-4 w-4 text-muted-foreground" />
                                  <span className="font-medium">Outcome:</span> {opportunity.outcome}
                                </div>
                              </div>
                              
                              {opportunity.volunteer_support && (
                                <div className="mt-4">
                                  <span className="font-medium">Volunteer Support:</span> {opportunity.volunteer_support}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                      {hasMoreOpportunities && (
                        <div 
                          ref={loadMoreRef}
                          className="w-full flex justify-center py-4 my-4 border-t pt-4"
                        >
                          {loadingOpportunities ? (
                            <div className="flex items-center justify-center">
                              <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-blue-500 border-r-2 rounded-full"></div>
                              <span className="text-sm">Loading more opportunities...</span>
                            </div>
                          ) : (
                            <div className="h-8 flex items-center justify-center">
                              <span className="text-xs text-muted-foreground">Scroll to load more</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">No opportunities found for this RFQ.</p>
                    </div>
                  )}
                </TabsContent>
              </div>
            </Tabs>
            
            <div className="p-4 border-t flex justify-end gap-2">
              {selectedResponse.status === 0 && (
                <Button 
                  variant="default" 
                  onClick={() => {
                    setSelectedResponse(null);
                    navigate(`/dashboard/corporate-events/rfqs/${selectedResponse.rfq_id}`);
                  }}
                >
                  Respond to RFQ
                </Button>
              )}
              <Button variant="outline" onClick={() => setSelectedResponse(null)}>
                Close
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
